import React from 'react';
import { clsx } from 'clsx';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const Dialog: React.FC<DialogProps> = ({ open, onOpenChange, children, className = '', size = 'md' }) => {
  if (!open) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className={clsx(
          'my-6 w-full overflow-hidden rounded-lg border border-border-default bg-surface-base shadow-xl',
          sizeClasses[size],
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

export interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogContent: React.FC<DialogContentProps> = ({
  children,
  className = '',
}) => (
  <div className={clsx('p-6', className)}>{children}</div>
);

export interface DialogHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({
  children,
  className = '',
}) => (
  <div className={clsx('mb-4 border-b border-border-subtle pb-4', className)}>
    {children}
  </div>
);

export interface DialogTitleProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogTitle: React.FC<DialogTitleProps> = ({
  children,
  className = '',
}) => <h2 className={clsx('text-lg font-semibold text-content-primary', className)}>{children}</h2>;

export interface DialogDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogDescription: React.FC<DialogDescriptionProps> = ({
  children,
  className = '',
}) => (
  <p className={clsx('mt-1 text-sm text-content-secondary', className)}>{children}</p>
);
